import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Slider } from "@/components/ui/slider";
import { useAuth } from "@/contexts/AuthContext";
import { SETTINGS } from "@/constants/testIds";
import { applyTenantBranding } from "@/lib/tenantHost";
import { CURRENCIES, formatMoney } from "@/lib/currency";
import { BACKGROUND_THEMES, applyBackgroundTheme } from "@/lib/backgroundThemes";
import { DashboardMiniPreview } from "@/components/DashboardMiniPreview";
import { Check, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Curated HSL space-separated presets — every palette works in BOTH light
// and dark shell modes (they only override --primary/--accent, not the
// background/foreground pair which flips with the shell mode toggle).
const PRESETS = [
  { name: "BizARGOS", primary: "22 90% 55%", accent: "275 60% 60%" },
  { name: "Clay", primary: "25 35% 55%", accent: "120 22% 42%" },
  { name: "Forest", primary: "150 30% 35%", accent: "35 55% 55%" },
  { name: "Rose", primary: "350 55% 55%", accent: "30 45% 55%" },
  { name: "Cobalt", primary: "215 60% 50%", accent: "180 40% 45%" },
  { name: "Sunset", primary: "12 70% 55%", accent: "40 75% 55%" },
  { name: "Charcoal", primary: "220 12% 30%", accent: "30 40% 55%" },
  // Light-mode-friendly picks (softer, more pastel — pop against white bg)
  { name: "Sage", primary: "150 25% 45%", accent: "45 55% 65%" },
  { name: "Lavender", primary: "260 55% 60%", accent: "180 45% 55%" },
  { name: "Peach", primary: "18 75% 62%", accent: "195 50% 55%" },
  { name: "Ocean", primary: "195 60% 45%", accent: "160 45% 45%" },
  { name: "Berry", primary: "330 55% 55%", accent: "260 45% 60%" },
];

export default function Branding() {
  const { tenant, setTenant, reload } = useAuth();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ name: "", tagline: "", primary_color: "", accent_color: "", logo_url: "", currency: "USD", background_theme: "none", wallpaper_overlay_pct: 55 });

  useEffect(() => {
    if (tenant) {
      setForm({
        name: tenant.name || "",
        tagline: tenant.tagline || "",
        primary_color: tenant.primary_color || "",
        accent_color: tenant.accent_color || "",
        logo_url: tenant.logo_url || "",
        currency: tenant.currency || "USD",
        background_theme: tenant.background_theme || "none",
        wallpaper_overlay_pct: Number.isFinite(tenant.wallpaper_overlay_pct) ? tenant.wallpaper_overlay_pct : 55,
      });
    }
  }, [tenant]);

  const applyPreset = (p) => {
    setForm((f) => ({ ...f, primary_color: p.primary, accent_color: p.accent }));
    applyTenantBranding({ primary_color: p.primary, accent_color: p.accent });
  };

  const pickBackground = (id) => {
    if (id === "custom") {
      // If a wallpaper is already on file, just flip the theme; otherwise open picker.
      if (tenant?.wallpaper_url) {
        setForm((f) => ({ ...f, background_theme: "custom" }));
        applyBackgroundTheme("custom", `${process.env.REACT_APP_BACKEND_URL}${tenant.wallpaper_url}`, form.wallpaper_overlay_pct);
        return;
      }
      fileInputRef.current?.click();
      return;
    }
    setForm((f) => ({ ...f, background_theme: id }));
    applyBackgroundTheme(id, tenant?.wallpaper_url ? `${process.env.REACT_APP_BACKEND_URL}${tenant.wallpaper_url}` : null, form.wallpaper_overlay_pct);
  };

  const setOverlayPct = (val) => {
    const n = Array.isArray(val) ? val[0] : val;
    setForm((f) => ({ ...f, wallpaper_overlay_pct: n }));
    // Live-apply so owner sees the dim change in real time
    if (form.background_theme === "custom" && tenant?.wallpaper_url) {
      applyBackgroundTheme("custom", `${process.env.REACT_APP_BACKEND_URL}${tenant.wallpaper_url}`, n);
    }
  };

  const uploadWallpaper = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Wallpaper must be under 5MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/tenant/wallpaper", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, background_theme: "custom" }));
      // Live apply so the owner sees the change before Save
      const abs = `${process.env.REACT_APP_BACKEND_URL}${data.wallpaper_url}`;
      applyBackgroundTheme("custom", abs);
      // Refresh tenant so wallpaper_url is on the context for future paints
      await reload();
      toast.success("Wallpaper uploaded — set as background.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    }
    setUploading(false);
  };

  const removeWallpaper = async () => {
    try {
      await api.delete("/tenant/wallpaper");
      setForm((f) => ({ ...f, background_theme: "none" }));
      applyBackgroundTheme("none", null);
      await reload();
      toast.success("Wallpaper removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed.");
    }
  };

  const save = async () => {
    try {
      const { data } = await api.patch("/tenant/branding", form);
      setTenant(data);
      await reload();
      toast.success("Branding updated.");
    } catch {
      toast.error("Could not save branding.");
    }
  };

  return (
    <div className="px-4 md:px-8 py-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">Studio · White Label</div>
        <h1 className="font-display text-4xl">Brand identity</h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-xl">
          Change the platform's identity for your workspace — colours propagate live across every module.
        </p>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 space-y-5">
        <div>
          <Label>Business name</Label>
          <Input data-testid={SETTINGS.brandName} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
        </div>
        <div>
          <Label>Tagline</Label>
          <Input data-testid={SETTINGS.brandTagline} value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="mt-1.5" />
        </div>
        <div>
          <Label>Logo URL (optional)</Label>
          <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://…" className="mt-1.5" />
        </div>

        <div>
          <Label>Display currency</Label>
          <p className="text-xs text-muted-foreground mt-1 mb-2">All money values across POS, dashboards, CRM and forms will render in this currency.</p>
          <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
            <SelectTrigger data-testid="brand-currency-trigger" className="w-full sm:w-80">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {CURRENCIES.map((c) => (
                <SelectItem key={c.code} value={c.code} data-testid={`brand-currency-item-${c.code}`}>
                  <span className="inline-flex items-center gap-2">
                    <span className="text-base leading-none">{c.flag}</span>
                    <span className="font-mono text-xs w-10 inline-block">{c.code}</span>
                    <span>{c.label}</span>
                    <span className="text-muted-foreground text-xs">· {c.symbol}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="mt-2 text-xs text-muted-foreground">
            Preview: <span data-testid="brand-currency-preview" className="text-foreground font-medium">{formatMoney(12345.67, form.currency)}</span>
          </div>
        </div>

        <div>
          <Label>Colour palette</Label>
          <div className="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.name}
                onClick={() => applyPreset(p)}
                className="group rounded-lg border border-border p-2 hover:border-primary transition-colors text-left"
              >
                <div className="flex gap-1 mb-1.5">
                  <span className="h-6 flex-1 rounded" style={{ backgroundColor: `hsl(${p.primary})` }} />
                  <span className="h-6 flex-1 rounded" style={{ backgroundColor: `hsl(${p.accent})` }} />
                </div>
                <div className="text-[10px] uppercase tracking-[0.18em]">{p.name}</div>
              </button>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Primary (HSL)</Label>
              <Input
                data-testid={SETTINGS.brandPrimary}
                value={form.primary_color}
                onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                placeholder="25 35% 55%"
                className="mt-1.5 font-mono text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">Accent (HSL)</Label>
              <Input
                data-testid={SETTINGS.brandAccent}
                value={form.accent_color}
                onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                placeholder="120 22% 42%"
                className="mt-1.5 font-mono text-xs"
              />
            </div>
          </div>
        </div>

        <div>
          <Label>Background theme</Label>
          <p className="text-xs text-muted-foreground mt-1 mb-2">Adds a subtle pattern behind every page — tints itself with your palette above. Choose <em>Custom</em> to upload your own studio photo.</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={uploadWallpaper}
            className="hidden"
            data-testid="brand-bg-file-input"
          />
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="brand-bg-grid">
            {BACKGROUND_THEMES.map((t) => {
              const active = form.background_theme === t.id;
              const hasWallpaper = t.id === "custom" && tenant?.wallpaper_url;
              const bgImage = hasWallpaper
                ? `linear-gradient(hsl(var(--background)/0.4), hsl(var(--background)/0.4)), url("${process.env.REACT_APP_BACKEND_URL}${tenant.wallpaper_url}")`
                : t.swatch;
              const wallpaperAbs = tenant?.wallpaper_url ? `${process.env.REACT_APP_BACKEND_URL}${tenant.wallpaper_url}` : null;
              return (
                <HoverCard key={t.id} openDelay={200} closeDelay={100}>
                  <HoverCardTrigger asChild>
                    <button
                      onClick={() => pickBackground(t.id)}
                      disabled={uploading && t.id === "custom"}
                      data-testid={`brand-bg-item-${t.id}`}
                      className={`group relative rounded-lg border p-2 text-left transition-colors ${active ? "border-primary ring-1 ring-primary/40" : "border-border hover:border-primary/60"} ${uploading && t.id === "custom" ? "opacity-60" : ""}`}
                      title={t.desc}
                    >
                      <span
                        aria-hidden
                        className="block h-14 rounded-md border border-border/60 mb-1.5 flex items-center justify-center"
                        style={{
                          background: bgImage,
                          backgroundSize: t.id === "custom" && hasWallpaper ? "cover" : t.id === "grid" ? "12px 12px" : t.id === "dots" ? "10px 10px" : undefined,
                          backgroundPosition: "center",
                        }}
                      >
                        {t.isCustom && !hasWallpaper && (
                          <Upload className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                        )}
                      </span>
                      <div className="text-[10px] uppercase tracking-[0.16em] font-semibold">
                        {t.label}
                        {uploading && t.id === "custom" && <span className="ml-1 text-muted-foreground">· uploading…</span>}
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {t.isCustom && !hasWallpaper ? "Click to upload PNG/JPG/WEBP · max 5MB" : t.desc}
                      </div>
                      {active && (
                        <span className="absolute top-1 right-1 inline-flex items-center justify-center h-4 w-4 rounded-full bg-primary text-primary-foreground">
                          <Check className="h-2.5 w-2.5" strokeWidth={3} />
                        </span>
                      )}
                    </button>
                  </HoverCardTrigger>
                  <HoverCardContent side="right" align="start" className="w-auto p-2" data-testid={`brand-bg-preview-${t.id}`}>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">
                      {t.label} · preview
                    </div>
                    <DashboardMiniPreview
                      themeId={t.id}
                      wallpaperUrl={wallpaperAbs}
                      overlayPct={form.wallpaper_overlay_pct}
                    />
                  </HoverCardContent>
                </HoverCard>
              );
            })}
          </div>
          {form.background_theme === "custom" && tenant?.wallpaper_url && (
            <div className="mt-3 rounded-md border border-border bg-secondary/40 p-3 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Legibility overlay</Label>
                <span className="font-mono text-xs text-foreground" data-testid="brand-bg-overlay-val">{form.wallpaper_overlay_pct}%</span>
              </div>
              <Slider
                min={0}
                max={90}
                step={5}
                value={[form.wallpaper_overlay_pct]}
                onValueChange={setOverlayPct}
                data-testid="brand-bg-overlay-slider"
              />
              <div className="flex justify-between text-[10px] text-muted-foreground">
                <span>0% · crisp photo</span>
                <span>90% · heavy dim</span>
              </div>
            </div>
          )}
          {tenant?.wallpaper_url && (
            <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <span>Custom wallpaper on file.</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={removeWallpaper}
                className="h-7 px-2 rounded-full text-destructive hover:text-destructive"
                data-testid="brand-bg-remove-wallpaper"
              >
                <Trash2 className="h-3 w-3 mr-1" /> Remove
              </Button>
            </div>
          )}
        </div>

        <div className="pt-2">
          <Button data-testid={SETTINGS.brandSave} onClick={save} className="rounded-full">
            Save branding
          </Button>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-border bg-card p-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-3">Preview</div>
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-md bg-primary text-primary-foreground grid place-items-center font-display text-3xl">
            {(form.name || "L").slice(0, 1)}
          </div>
          <div>
            <div className="font-display text-3xl leading-tight">{form.name || "Your business"}</div>
            <div className="text-sm text-muted-foreground">{form.tagline || "Tagline appears here"}</div>
          </div>
          <div className="ml-auto flex gap-2">
            <Button className="rounded-full">Primary action</Button>
            <Button variant="outline" className="rounded-full">Secondary</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
